/**
 * Step-up (11 §2.4, HANDOFF §0.3, KEYCLOAK-THEME §3). Every step-up operation goes through `useStepUp().run`:
 *
 * 1. send the request (with an `Idempotency-Key`); if the API answers 401 `step-up-required`,
 * 2. show "Confirm it's you" with what is confirmed and one button, "Confirm with your second factor";
 * 3. store the pending action as a descriptor (transient store, 10 minutes, never tokens, never secrets) and
 *    redirect to Keycloak with `acr_values=stepup` (a redirect: COOP same-origin rules out a popup);
 * 4. back on `returnTo`, take the descriptor and retry the action once with the new token (`acr=stepup`), the
 *    same `Idempotency-Key` and the same workspace (`X-Brevalt-Tenant`); hand the result to the action's handler
 *    (`useStepUpResult`), errors and handler-less results to `onResult`.
 *
 * A descriptor is replayed only right after the redirect back from the step-up (`session.takeStepUpReturn()`),
 * for the same person (`sub`) and page; an abandoned step-up is dropped on the next load.
 *
 * Bodies with secret-looking fields (password, token, key, secret) are refused unless the action is marked
 * `sensitive`: then nothing of the body is stored, and after the step-up the handler is asked to collect it again.
 * GET downloads (full SBOM, technical file, export) save the file after the replay.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { Button, DialogContent, DialogRoot } from "@brevalt/ui";
import { isStepUpRequired, request } from "@brevalt/api-client";
import { isSafeAppPath } from "../routing";
import { workspace } from "../workspace";
import { useErrorToast } from "../errors/toasts";
import { useAuth } from "./provider";
import { transientStore, type StepUpFormKey } from "./transient-store";

/** One step-up operation. `action` is already translated ("Mark the early warning as submitted"). */
export interface StepUpRequest {
  action: string;
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  body?: unknown;
  /** Key of the result handler (`useStepUpResult`), e.g. "api-token.created": it also gets the replayed result. */
  handler?: string;
  /** The handler also gets an error of the replay (it shows it where the action was, e.g. inline in a form). */
  handlesErrors?: boolean;
  /**
   * A download: the response is saved under this name (also after the step-up); `mediaType` for `Accept`.
   * A large one (the technical file, up to 2 GB) sets `pickSink`: once the response has arrived, the person picks a
   * file and the body streams into it (`showSaveFilePicker`, so no empty file is left when the step-up comes
   * first); where there is none, it goes through memory up to `memoryLimit` bytes (`Content-Length`), above that
   * `DownloadTooLargeError`. When the picker needs a new click (the answer came after the click's activation ran
   * out), `run` resolves with a `PendingDownload` holding the open response. Such a download is never replayed
   * after the step-up: the handler gets `reask` and offers the download again (a fresh click for the picker).
   */
  download?: {
    filename: string;
    mediaType?: string;
    pickSink?: () => Promise<PickResult>;
    memoryLimit?: number;
  };
  /** The body holds a secret: it is never stored; after the step-up the handler re-asks for it (`reask`). */
  sensitive?: boolean;
  /**
   * Not replayed after the step-up: the handler gets `reask` and decides (e.g. signing checks first that the
   * version shown is still the current one). The body is not stored; the page keeps what it needs itself.
   */
  reaskAfterStepUp?: boolean;
  /**
   * A form the page needs back after the redirect (no secrets): written to its own transient slot only when the
   * redirect starts (a cancelled step-up leaves nothing), never inside the descriptor.
   */
  keep?: { key: StepUpFormKey; values: unknown; ttlMs: number };
}

/** A named multi-step action (e.g. sign out everywhere) instead of one request. */
export interface StepUpNamedAction {
  action: string;
  named: "logout-everywhere";
  handler?: string;
}

type Pending = (StepUpRequest | StepUpNamedAction) & { idempotencyKey?: string; tenantId?: string | null };
type Descriptor = Pending & { reask?: true; returnTo: string; createdAt: string; sub: string };

/**
 * What a handler receives: the result (with the replayed request's body, e.g. which plan a preview was for), the
 * error, or `reask` (a sensitive body was not kept).
 */
export type StepUpOutcome = { result: unknown; body?: unknown } | { error: unknown } | { reask: true };

export const STEP_UP_TTL_MS = 10 * 60_000;
/** A replayed result waits this long for its handler to mount (then it is dropped). */
const WAITING_TTL_MS = 60_000;

const SECRET_FIELD = /secret|password|passphrase|token|credential|private_?key|key$|pem$|^pin$|otp$/i;

/** The first field name in `body` (deep) that looks like it holds a secret. */
export function secretField(body: unknown): string | null {
  if (body === null || typeof body !== "object") return null;
  for (const [k, v] of Object.entries(body)) {
    if (SECRET_FIELD.test(k)) return k;
    const inner = secretField(v);
    if (inner) return inner;
  }
  return null;
}

/** Where a streamed download goes (a `FileSystemFileHandle`'s writable). */
export interface StreamSink {
  createWritable: () => Promise<WritableStream<Uint8Array>>;
}

export type PickResult = StreamSink | "cancelled" | "needs-gesture" | null;

/**
 * A file to stream a large download into, picked by the person (Chromium's File System Access API). Null where the
 * browser has none: the download then goes through memory. "needs-gesture" when the click's user activation ran out
 * before the response came (Chromium allows the picker about 5 s after a click): the caller asks for a new click.
 */
export async function pickDownloadSink(filename: string, mediaType: string, extension: string): Promise<PickResult> {
  const picker = (
    window as unknown as {
      showSaveFilePicker?: (o: {
        suggestedName: string;
        types: { description: string; accept: Record<string, string[]> }[];
      }) => Promise<StreamSink>;
    }
  ).showSaveFilePicker;
  if (typeof picker !== "function") return null;
  try {
    return await picker({
      suggestedName: filename,
      types: [{ description: filename, accept: { [mediaType]: [extension] } }],
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") return "cancelled";
    if (e instanceof DOMException && (e.name === "SecurityError" || e.name === "NotAllowedError"))
      return "needs-gesture";
    return null;
  }
}

/** How long an answered download waits for "Choose where to save" before its body is dropped. */
export const PENDING_DOWNLOAD_MS = 60_000;

/**
 * An answered download whose file couldn't be picked yet (the click's activation ran out): the response stays open
 * until `save()` (from a new click) picks the file and streams it, or until it expires and the body is dropped.
 */
export class PendingDownload {
  private done = false;
  private readonly timer: ReturnType<typeof setTimeout>;

  constructor(
    private readonly res: Response,
    private readonly pick: () => Promise<PickResult>,
    timeoutMs = PENDING_DOWNLOAD_MS,
  ) {
    this.timer = setTimeout(() => {
      this.cancel();
    }, timeoutMs);
  }

  get expired(): boolean {
    return this.done;
  }

  cancel(): void {
    if (this.done) return;
    this.done = true;
    clearTimeout(this.timer);
    void this.res.body?.cancel().catch(() => undefined);
  }

  /** Picks the file (call it from a click) and streams the body into it. */
  async save(): Promise<"saved" | "cancelled" | "expired" | "needs-gesture" | "no-picker"> {
    if (this.done) return "expired";
    const sink = await this.pick();
    if (sink === "needs-gesture") return "needs-gesture";
    if (sink === null) {
      // The picker failed for another reason (or vanished): nothing to stream into.
      this.cancel();
      return "no-picker";
    }
    if (sink === "cancelled" || !this.res.body) {
      this.cancel();
      return sink === "cancelled" ? "cancelled" : "expired";
    }
    this.done = true;
    clearTimeout(this.timer);
    await this.res.body.pipeTo(await sink.createWritable());
    return "saved";
  }
}

/** A download too large to hold in memory where the browser can't stream it to a file. */
export class DownloadTooLargeError extends Error {
  constructor(readonly size: number) {
    super(`download of ${String(size)} bytes is over the in-memory limit`);
    this.name = "DownloadTooLargeError";
  }
}

/** Saves a downloaded file (the browser's download, from memory). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 30_000);
}

async function send(r: StepUpRequest, idempotencyKey: string, tenantId: string | null | undefined): Promise<unknown> {
  const headers: Record<string, string> = { "Idempotency-Key": idempotencyKey };
  if (tenantId) headers["X-Brevalt-Tenant"] = tenantId;
  if (r.body !== undefined && r.method !== "GET") headers["Content-Type"] = "application/json";
  if (r.download) headers.Accept = r.download.mediaType ?? "*/*";
  const init: RequestInit = {
    method: r.method,
    headers,
    ...(r.body === undefined || r.method === "GET" ? {} : { body: JSON.stringify(r.body) }),
  };
  const d = r.download;
  if (d?.pickSink || d?.memoryLimit !== undefined) {
    const res = await request<Response>("customer", r.path, init, { as: "response" });
    const sink = d.pickSink ? await d.pickSink() : null;
    // Not waiting for the cancel: the connection may hold until the server notices.
    const drop = () => void res.body?.cancel().catch(() => undefined);
    if (sink === "cancelled") {
      drop();
      return null;
    }
    if (sink === "needs-gesture") return new PendingDownload(res, d.pickSink ?? (() => Promise.resolve(null)));
    if (sink) {
      if (!res.body) throw new Error("download without a body");
      await res.body.pipeTo(await sink.createWritable());
      return true;
    }
    const size = Number(res.headers.get("content-length") ?? "NaN");
    if (d.memoryLimit !== undefined && (!Number.isFinite(size) || size > d.memoryLimit)) {
      drop();
      throw new DownloadTooLargeError(Number.isFinite(size) ? size : -1);
    }
    saveBlob(await res.blob(), d.filename);
    return true;
  }
  const result = await request<unknown>("customer", r.path, init, r.download ? { as: "blob" } : {});
  if (r.download && result instanceof Blob) saveBlob(result, r.download.filename);
  return result;
}

type Handler = (outcome: StepUpOutcome) => void;

interface Ctx {
  ask: (pending: Pending) => void;
  register: (key: string, handler: Handler) => () => void;
}
const StepUpContext = createContext<Ctx | null>(null);

/** Named actions the app can resume after the step-up (registered by the provider's owner). */
export type NamedActionRunner = (named: StepUpNamedAction["named"]) => Promise<unknown>;

export function StepUpProvider({
  children,
  currentPath = () => window.location.pathname + window.location.search + window.location.hash,
  runNamed,
  onResult,
}: {
  children: ReactNode;
  currentPath?: () => string;
  runNamed?: NamedActionRunner;
  /** The outcome of a resumed action without a handler (toast, refetch): `error` is null on success. */
  onResult?: (descriptor: { action: string }, result: unknown, error: unknown) => void;
}) {
  const { t } = useTranslation();
  const session = useAuth();
  const toastError = useErrorToast();
  const [pending, setPending] = useState<Pending | null>(null);
  const [leaving, setLeaving] = useState(false);
  const returnFocus = useRef<HTMLElement | null>(null);
  const handlers = useRef(new Map<string, Handler>());
  // Outcomes for handlers that are not mounted yet (the page of `returnTo` may still be loading): delivered only
  // on that page, in that workspace, within WAITING_TTL_MS (a once-only token never shows anywhere else).
  const waiting = useRef(
    new Map<string, { outcome: StepUpOutcome; returnTo: string; tenantId: string | null; at: number }>(),
  );

  const ask = useCallback((p: Pending) => {
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPending(p);
  }, []);

  const register = useCallback(
    (key: string, handler: Handler) => {
      handlers.current.set(key, handler);
      const entry = waiting.current.get(key);
      if (entry) {
        waiting.current.delete(key);
        const fresh = Date.now() - entry.at < WAITING_TTL_MS;
        if (fresh && entry.returnTo === currentPath() && entry.tenantId === (workspace.get() ?? null))
          handler(entry.outcome);
      }
      return () => {
        if (handlers.current.get(key) === handler) handlers.current.delete(key);
      };
    },
    [currentPath],
  );

  const deliver = useCallback(
    (d: Descriptor, outcome: StepUpOutcome) => {
      // Errors (and results of actions without a handler) go to the general handler: toast, refetch; a handler that
      // asked for them (`handlesErrors`) gets the error instead.
      const ownErrors = "handlesErrors" in d && d.handlesErrors === true;
      if (("error" in outcome && !ownErrors) || !d.handler) {
        if (!("reask" in outcome))
          onResult?.(d, "result" in outcome ? outcome.result : undefined, "error" in outcome ? outcome.error : null);
        return;
      }
      const h = handlers.current.get(d.handler);
      if (h) h(outcome);
      else
        waiting.current.set(d.handler, { outcome, returnTo: d.returnTo, tenantId: d.tenantId ?? null, at: Date.now() });
    },
    [onResult],
  );

  const confirm = () => {
    if (!pending) return;
    const path = currentPath();
    const returnTo = isSafeAppPath(path) ? path : "/";
    const sub = session.sessionUser?.sub;
    if (!sub) return;
    const { keep, ...rest } = pending as Pending & { keep?: StepUpRequest["keep"] };
    const descriptor: Descriptor = { ...rest, returnTo, sub, createdAt: new Date().toISOString() };
    // A large download is not replayed: the picker needs a fresh click, so its handler offers it again.
    if ("method" in descriptor && descriptor.download?.pickSink) {
      const { pickSink: _pick, ...download } = descriptor.download;
      descriptor.download = download;
      descriptor.reask = true;
    }
    if ("method" in descriptor && descriptor.reaskAfterStepUp === true) {
      delete descriptor.body;
      descriptor.reask = true;
    }
    if ("method" in descriptor && descriptor.sensitive === true) {
      // A secret is never written down: after the step-up the handler asks for it again.
      delete descriptor.body;
      descriptor.reask = true;
    }
    try {
      transientStore.put("stepup", descriptor, STEP_UP_TTL_MS);
      if (keep) transientStore.put(keep.key, keep.values, keep.ttlMs);
    } catch (e) {
      // Nothing half-written: without its form the replay would come back to an empty page.
      transientStore.take("stepup");
      setPending(null);
      toastError(e);
      return;
    }
    setLeaving(true);
    session.stepUp(returnTo).catch((e: unknown) => {
      // The redirect did not start (Keycloak unreachable): nothing is pending any more.
      transientStore.take("stepup");
      if (keep) transientStore.take(keep.key);
      setLeaving(false);
      setPending(null);
      toastError(e);
    });
  };

  // Back/forward cache: the dialog of a page that left for Keycloak and came back from the cache.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      // Back without finishing the step-up: the stored action is abandoned.
      transientStore.take("stepup");
      setLeaving(false);
      setPending(null);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  // Resume once, right after the redirect back from the step-up, for the same person and page (the descriptor is
  // single use and expires after 10 minutes; any other load drops it).
  const resumed = useRef(false);
  useEffect(() => {
    if (resumed.current) return;
    resumed.current = true;
    const stepUpReturn = session.takeStepUpReturn();
    const d = transientStore.take("stepup") as Descriptor | null;
    if (!d || !stepUpReturn) return;
    if (d.sub !== session.sessionUser?.sub || d.returnTo !== currentPath()) return;
    if (d.reask) {
      deliver(d, { reask: true });
      return;
    }
    const run =
      "named" in d
        ? runNamed
          ? runNamed(d.named)
          : Promise.reject(new Error("no runner"))
        : send(d, d.idempotencyKey ?? crypto.randomUUID(), d.tenantId);
    run.then(
      (result: unknown) => {
        deliver(d, { result, ...("body" in d && d.body !== undefined ? { body: d.body } : {}) });
      },
      (error: unknown) => {
        deliver(d, { error });
      },
    );
  }, [session, currentPath, runNamed, deliver]);

  return (
    <StepUpContext.Provider value={{ ask, register }}>
      {children}
      <DialogRoot
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !leaving) setPending(null);
        }}
      >
        {pending && (
          <DialogContent
            title={t("ui.confirm_it_s_you")}
            description={t("shell.action_is_recorded_in_the_audit_log", { action: pending.action })}
            returnFocusRef={returnFocus}
            footer={
              <>
                <Button
                  variant="ghost"
                  disabled={leaving}
                  onClick={() => {
                    setPending(null);
                  }}
                >
                  {t("ui.cancel")}
                </Button>
                <Button variant="primary" icon={ShieldCheck} loading={leaving} onClick={confirm}>
                  {t("shell.confirm_with_your_second_factor")}
                </Button>
              </>
            }
          />
        )}
      </DialogRoot>
    </StepUpContext.Provider>
  );
}

export function useStepUp() {
  const ctx = useContext(StepUpContext);
  if (!ctx) throw new Error("useStepUp outside StepUpProvider");
  /**
   * Runs a step-up operation. Resolves with the response (`null` for an empty one, e.g. a 204 DELETE), or with
   * `undefined` when the step-up redirect takes over (the action is retried after the return and its handler gets
   * the result). Other errors reject.
   */
  const run = useCallback(
    async <T,>(r: StepUpRequest): Promise<T | undefined> => {
      const secret = secretField(r.body);
      if (secret && !r.sensitive) {
        throw new Error(`step-up: field "${secret}" looks secret; mark the action sensitive (it is then never stored)`);
      }
      if (r.sensitive && !r.handler) {
        throw new Error("step-up: a sensitive action needs a handler (it re-asks for the secret after the step-up)");
      }
      if (r.reaskAfterStepUp && !r.handler) {
        throw new Error("step-up: an action that is asked again after the step-up needs a handler");
      }
      if (r.download?.pickSink && !r.handler) {
        throw new Error("step-up: a streamed download needs a handler (it is offered again after the step-up)");
      }
      const idempotencyKey = crypto.randomUUID();
      // The workspace of this action: the replay after the redirect goes to the same one.
      const tenantId = workspace.get();
      try {
        // An empty answer (204) resolves with null, so callers can tell it from the redirect (undefined).
        return ((await send(r, idempotencyKey, tenantId)) ?? null) as T;
      } catch (e) {
        if (!isStepUpRequired(e)) throw e;
        ctx.ask({ ...r, idempotencyKey, tenantId });
        return undefined;
      }
    },
    [ctx],
  );
  /** A named action that already met 401 step-up-required (e.g. inside sign out everywhere). */
  const require = useCallback(
    (named: StepUpNamedAction) => {
      ctx.ask(named);
    },
    [ctx],
  );
  return { run, require };
}

/**
 * The result handler of a step-up action (`StepUpRequest.handler`). It also receives the outcome of the action
 * replayed after the step-up redirect, e.g. to show a new API token once.
 */
export function useStepUpResult(key: string, handler: (outcome: StepUpOutcome) => void): void {
  const ctx = useContext(StepUpContext);
  if (!ctx) throw new Error("useStepUpResult outside StepUpProvider");
  const ref = useRef(handler);
  useLayoutEffect(() => {
    ref.current = handler;
  });
  useLayoutEffect(
    () =>
      ctx.register(key, (outcome) => {
        ref.current(outcome);
      }),
    [ctx, key],
  );
}
